import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import "./SearchSpanGame.css";

const SearchSpanGame = () => {

  const navigate = useNavigate();
  const topRef = useRef(null);

  const [numbers, setNumbers] = useState([]);
  const [currentTarget, setCurrentTarget] = useState(1);
  const [startTime, setStartTime] = useState(null);
  const [time, setTime] = useState(0);
  const [errors, setErrors] = useState(0);
  const [score, setScore] = useState(0);
  const [started, setStarted] = useState(false);
  const [countdown, setCountdown] = useState(null);
  const [page, setPage] = useState("game");
  const [phase, setPhase] = useState("popup");
  const [memoTime, setMemoTime] = useState(10);
  const [leaderboard, setLeaderboard] = useState([]);
  const [userRank, setUserRank] = useState(null);
  const [clickedNums, setClickedNums] = useState([]);

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Lock body scroll while popup/countdown overlays are showing
  // so nothing shifts when they mount/unmount around "GO!"
  useEffect(() => {
    if (phase === "popup" || phase === "countdown") {
      document.body.classList.add("overlay-lock");
    } else {
      document.body.classList.remove("overlay-lock");
    }
    return () => {
      document.body.classList.remove("overlay-lock");
    };
  }, [phase]);

  useEffect(() => {
    const arr = Array.from({ length: 50 }, (_, i) => i + 1);
    const shuffled = arr.sort(() => Math.random() - 0.5);
    setNumbers(shuffled);
  }, []);

  const handlePopupOk = () => {
    setPhase("memorize");
    let t = 10;
    setMemoTime(t);

    const memoInterval = setInterval(() => {
      t--;
      setMemoTime(t);
      if (t <= 0) {
        clearInterval(memoInterval);
        setTimeout(() => {
          startCountdown();
        }, 800);
      }
    }, 1000);
  };

  const startCountdown = () => {
    setPhase("countdown");
    let count = 3;
    setCountdown(count);

    const interval = setInterval(() => {
      count--;
      if (count > 0) {
        setCountdown(count);
      } else {
        clearInterval(interval);
        setCountdown("GO!");
        setTimeout(() => {
          setCountdown(null);
          setPhase("game");
          setStartTime(performance.now());
          setStarted(true);
        }, 1000);
      }
    }, 1000);
  };

  // ✅ Timer - fixed
  useEffect(() => {
    if (phase !== "game" || !started || !startTime) return;

    const interval = setInterval(() => {
      const now = performance.now();
      const elapsed = (now - startTime) / 1000;

      if (elapsed >= 60) {
        clearInterval(interval);
        setStarted(false);
        setTime("60.00");
        finishGame(currentTarget - 1);
        return;
      }

      setTime(elapsed.toFixed(2));
    }, 100);

    return () => clearInterval(interval);
  }, [startTime, phase, currentTarget, started]);

  const handleClick = (num) => {
    if (!started || phase !== "game" || page !== "game") return;

    if (num === currentTarget) {
      const newScore = currentTarget;
      setScore(newScore);
      setClickedNums(prev => [...prev, num]); // ✅ track clicked

      if (currentTarget === 50) {
        finishGame(newScore);
      } else {
        setCurrentTarget(prev => prev + 1);
      }
    } else {
      setErrors(prev => prev + 1);
    }
  };

  const fetchLeaderboard = async () => {
    try {
      const email = localStorage.getItem("rg_email");
      const res = await axios.get(`/searchspanleaderboard?email=${email}`);
      setLeaderboard(res.data.leaderboard || []);
      setUserRank(res.data.userRank);
    } catch (e) {
      console.log(e);
    }
  };

  useEffect(() => {
    fetchLeaderboard();
  }, []);

  const finishGame = async (finalScore = score) => {
    setPage("result");
    const email = localStorage.getItem("rg_email");
    try {
      await axios.post("/save-search-span", { email, finalNumber: finalScore });
      await fetchLeaderboard();
    } catch (e) {
      console.log(e);
    }
  };

  const handleTryAgain = () => window.location.reload();
  const handleExit = () => navigate("/search-span");

  return (
    <div className="search-span-container">

      <div ref={topRef} />

      {/* ✅ POPUP */}
      {phase === "popup" && (
        <div className="popup-overlay">
          <div className="popup-box">
            <h2>Get Ready!</h2>
            <p>You have <strong>10 seconds</strong> to memorize the grid.</p>
            <p>Then find numbers <strong>1 → 50</strong> in order!</p>
            <button className="popup-ok-btn" onClick={handlePopupOk}>
              OK, Let's Go!
            </button>
          </div>
        </div>
      )}

      {page === "game" && (
        <>

          {/* ✅ Fixed-height status header — memo timer / target+timer
              always occupy the SAME reserved space so the grid
              below never shifts up or down between phases */}
          <div className="status-header">

            {phase === "memorize" && (
              <div className="memo-timer">
                Memorize! {memoTime}s
              </div>
            )}

            {phase === "game" && (
              <>
                <div className="target-box">
                  Find Number: <span>{currentTarget}</span>
                </div>
                <div className="timer">
                  Time Left: {Math.max(0, (60 - parseFloat(time))).toFixed(2)}s
                </div>
              </>
            )}

          </div>

          {/* ✅ 3..2..1..GO overlay */}
          {countdown && (
            <div className="countdown-overlay">
              <div className="countdown-inner">
                <div className="countdown-text">{countdown}</div>
                {countdown === "GO!" && (
                  <div className="lets-play-text">Let's Play!</div>
                )}
              </div>
            </div>
          )}

          {/* ✅ Grid - no gray shade on click */}
          <div className="grid">
            {numbers.map((num, i) => (
              <div
                key={i}
                className={`cell ${
                  clickedNums.includes(num) ? "clicked-cell" : ""
                }`}
                onClick={() => handleClick(num)}
              >
                {num}
              </div>
            ))}
          </div>

          {phase === "game" && (
            <div className="errors">Errors: {errors}</div>
          )}

        </>
      )}

      {/* ✅ RESULT PAGE */}
      {page === "result" && (
        <div className="result-big-container">

          <div className="leaderboard-box">
            <h3>Leaderboard</h3>
            <table>
              <thead>
                <tr>
                  <th>Rank</th>
                  <th>Name</th>
                  <th>Final Number</th>
                  <th>Medal</th>
                </tr>
              </thead>
              <tbody>
                {leaderboard.slice(0, 5).map((p, i) => {

                    let medal = "";

                    if (p.rank === 1) {
                      medal = "🥇";
                    } else if (p.rank === 2) {
                      medal = "🥈";
                    } else if (p.rank === 3) {
                      medal = "🥉";
                    }

                    return (
                      <tr key={i}>
                        <td>{p.rank}</td>
                        <td>{p.name}</td>
                        <td>{p.score}</td>
                        <td>{medal}</td>
                      </tr>
                    );

                  })}
              </tbody>
            </table>

            {userRank && (
              <div className="your-rank-box">
                Rank: {userRank.rank} <br />
                Final Number: {score} <br />

              </div>
            )}
          </div>

          <div className="results-box">
            <h2>Your Result</h2>
            <p>Time: {Math.min(60, parseFloat(time)).toFixed(2)}s</p>
            <p>Final Number: {score}</p>
            <button onClick={handleTryAgain}>Try Again</button>
            <button onClick={handleExit}>Exit</button>
          </div>

        </div>
      )}

    </div>
  );
};

export default SearchSpanGame;
